import React, { useState, useEffect } from "react";
import API from "../../../Utils/api";
import UploadImg from "../../../Utils/UploadImg";
import toast from "react-hot-toast";

const EditHeroModal = ({ hero, onClose, onSuccess }) => {
  const [form, setForm] = useState({ heading: "" });
  const [imageFiles, setImageFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hero) {
      setForm({ heading: hero.heading || "" });
      setPreviews(hero.image || []);
    }
  }, [hero]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files);
    setImageFiles(files);
    setPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const removePreview = (index) => {
    const isNew = imageFiles.length > 0;
    if (isNew) {
      setImageFiles((prev) => prev.filter((_, i) => i !== index));
    }
    setPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    setLoading(true);
    try {
      let imageUrls = hero.image || [];

      if (imageFiles.length > 0) {
        imageUrls = await Promise.all(imageFiles.map((f) => UploadImg(f)));
      } else {
        // Keep only previews that are still shown (user may have removed some)
        imageUrls = previews;
      }

      await API.patch(`/hero/update/${hero._id}`, {
        ...form,
        image: imageUrls,
      });

      onSuccess();
      toast.success("Your Hero Section has been Updated successfully! 🎉");
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong");
      toast.error(
        err.response?.data?.message ||
          "Unable to publish your Hero Section right now. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-green-50 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Edit Hero Section</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blog Images
            </label>
            <div
              onClick={() =>
                document.getElementById("edit-hero-images").click()
              }
              className="border-2 border-dashed border-gray-300 hover:border-green-400 rounded-xl p-4 cursor-pointer transition"
            >
              {previews.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {previews.map((p, i) => (
                    <div key={i} className="relative">
                      <img
                        src={p}
                        alt=""
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removePreview(i);
                        }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 text-2xl">
                    +
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-400 py-4">
                  <p className="text-3xl">🖼️</p>
                  <p className="text-sm mt-1">Click to change images</p>
                </div>
              )}
            </div>
            <input
              id="edit-hero-images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleImages}
              className="hidden"
            />
          </div>

          {/* Heading */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heading <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="heading"
              value={form.heading}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2.5 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium transition disabled:opacity-60"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditHeroModal;
